import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Lock, Mail, User, Eye, EyeOff, KeyRound } from 'lucide-react';
import { useCart } from '../context/CartContext'; 

const CustomerAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerAuthSync } = useCart();
  const googleBtnRef = useRef(null);
  
  const [isLoginView, setIsLoginView] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  // PASTE YOUR GOOGLE CLIENT ID HERE:
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "88874516093-mrqln11ucdqvs9d51g2cij93e0pq1378.apps.googleusercontent.com";

  // OTP Verification Modal State
  const [isOtpStep, setIsOtpStep] = useState(false);
  const [otpCode, setOtpCode] = useState('');

  // Form Fields State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (authError) setAuthError('');
  };

  // Helper: Decode Google JWT token to extract email & name
  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  };

  // 1. Google OAuth Callback: runs when account is picked in Google's official popup
  const handleGoogleCredentialResponse = async (response) => {
    setIsSubmitting(true);
    setAuthError('');
    try {
      const payload = parseJwt(response.credential);
      if (!payload || !payload.email) {
        throw new Error('Could not retrieve account details from Google.');
      }

      const res = await fetch('https://api.modischekledings.com/api/auth/social-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: payload.name || payload.given_name || 'Google User',
          email: payload.email,
          provider: 'google'
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Google authentication failed.');

      localStorage.setItem('modische_token', data.token);
      localStorage.setItem('modische_user', JSON.stringify({
        name: data.user.name,
        email: data.user.email,
        isAdmin: data.user.isAdmin === true || data.user.role === 'admin'
      }));

      if (triggerAuthSync) triggerAuthSync();
      navigate(location.state?.from || '/', { replace: true });
    } catch (err) {
      setAuthError(err.message || 'Google sign-in error.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 2. Initialize and render Google's official button
  useEffect(() => {
    const renderGoogleButton = () => {
      if (window.google?.accounts?.id && googleBtnRef.current) {
        googleBtnRef.current.innerHTML = ''; // prevent duplicate buttons on tab switch
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredentialResponse,
          auto_select: false
        });

        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: 380,
          text: 'continue_with',
          shape: 'rectangular'
        });
      }
    };

    if (window.google) {
      renderGoogleButton();
    } else {
      const timer = setInterval(() => {
        if (window.google) {
          clearInterval(timer);
          renderGoogleButton();
        }
      }, 250);
      return () => clearInterval(timer);
    }
  }, [isLoginView, GOOGLE_CLIENT_ID]);

  // 3. Email/Password Submission
  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAuthError('');
    setInfoMessage('');

    if (!isLoginView && formData.password !== formData.confirmPassword) {
      setAuthError("Passwords do not match.");
      setIsSubmitting(false);
      return;
    }

    if (!isLoginView && !isOtpStep) {
      try {
        const otpRes = await fetch('https://api.modischekledings.com/api/auth/send-otp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email })
        });
        const otpData = await otpRes.json();
        if (!otpRes.ok) throw new Error(otpData.message || 'Failed to send verification code.');

        setIsOtpStep(true);
        setInfoMessage(`Verification code sent to ${formData.email}`);
      } catch (err) {
        setAuthError(err.message || 'Failed to initialize email verification.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const endpoint = isLoginView ? '/api/auth/login' : '/api/auth/register';
    const requestPayload = isLoginView 
      ? { email: formData.email, password: formData.password }
      : { name: formData.name, email: formData.email, password: formData.password, otp: otpCode };

    try {
      const response = await fetch(`https://api.modischekledings.com${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Authentication sequence failed.');

      localStorage.setItem('modische_token', data.token);
      localStorage.setItem('modische_user', JSON.stringify({
        name: data.user.name,
        email: data.user.email,
        isAdmin: data.user.isAdmin === true || data.user.role === 'admin'
      }));

      if (triggerAuthSync) triggerAuthSync();
      navigate(location.state?.from || '/', { replace: true });
    } catch (err) {
      setAuthError(err.message || 'Server connection timeout.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-[85vh] bg-[#F5F5DC] pt-36 pb-20 px-4 flex flex-col justify-center items-center font-sans">
      <div className="w-full max-w-md bg-[#E5D3B3] p-8 md:p-10 rounded-sm shadow-2xl border border-[#2C1E12]/10 relative">
        
        <button 
          type="button"
          onClick={() => navigate('/')}
          className="absolute top-4 left-4 flex items-center gap-1.5 text-[8px] uppercase tracking-widest text-[#664229] hover:text-[#2C1E12] transition font-bold bg-transparent border-none cursor-pointer"
        >
          <ArrowLeft size={10} /> Back To Gallery
        </button>

        <div className="text-center mb-6 border-b border-[#2C1E12]/10 pb-4 pt-4">
          <h2 className="text-2xl font-black tracking-tight text-[#2C1E12] uppercase">
            {isLoginView ? 'Log In' : 'Sign Up'}
          </h2>
          <p className="text-[9px] uppercase tracking-widest text-[#664229] opacity-70 mt-0.5">
            {isLoginView ? 'Access your personal lookbook' : 'Join Modische Kledings'}
          </p>
        </div>

        {/* NATIVE GOOGLE IDENTITY SERVICES BUTTON CONTAINER */}
        <div className="flex justify-center mb-5 min-h-[44px]">
          <div ref={googleBtnRef} className="w-full flex justify-center"></div>
        </div>

        <div className="relative flex py-2 items-center mb-4">
          <div className="flex-grow border-t border-[#2C1E12]/15"></div>
          <span className="flex-shrink mx-3 text-[9px] uppercase tracking-widest text-[#664229] font-bold">Or with Email</span>
          <div className="flex-grow border-t border-[#2C1E12]/15"></div>
        </div>

        {/* Primary Action Form */}
        <form onSubmit={handleAuthSubmit} className="space-y-4">
          
          {!isLoginView && !isOtpStep && (
            <div className="space-y-1 text-left">
              <label className="text-[9px] uppercase tracking-wider font-black text-[#664229]">Full Name</label>
              <div className="relative flex items-center">
                <User className="absolute left-3 w-4 h-4 text-[#664229]/50" />
                <input 
                  type="text" 
                  name="name"
                  required
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full bg-[#F5F5DC] border border-[#664229]/20 rounded-sm py-3 pl-10 pr-4 text-xs font-sans text-[#2C1E12] focus:outline-none focus:border-[#2C1E12] transition shadow-inner"
                />
              </div>
            </div>
          )}

          {!isOtpStep && (
            <div className="space-y-1 text-left">
              <label className="text-[9px] uppercase tracking-wider font-black text-[#664229]">Email Address</label>
              <div className="relative flex items-center">
                <Mail className="absolute left-3 w-4 h-4 text-[#664229]/50" />
                <input 
                  type="email" 
                  name="email"
                  required
                  placeholder="your.email@domain.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-[#F5F5DC] border border-[#664229]/20 rounded-sm py-3 pl-10 pr-4 text-xs font-sans text-[#2C1E12] focus:outline-none focus:border-[#2C1E12] transition shadow-inner"
                />
              </div>
            </div>
          )}

          {!isOtpStep && (
            <div className="space-y-1 text-left">
              <label className="text-[9px] uppercase tracking-wider font-black text-[#664229]">Password</label>
              <div className="relative flex items-center">
                <Lock className="absolute left-3 w-4 h-4 text-[#664229]/50" />
                <input 
                  type={showPassword ? "text" : "password"} 
                  name="password"
                  required
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full bg-[#F5F5DC] border border-[#664229]/20 rounded-sm py-3 pl-10 pr-10 text-xs font-sans text-[#2C1E12] focus:outline-none focus:border-[#2C1E12] transition shadow-inner"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 text-[#664229]/60 hover:text-[#2C1E12] bg-transparent border-none cursor-pointer"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {!isLoginView && !isOtpStep && (
            <div className="space-y-1 text-left">
              <label className="text-[9px] uppercase tracking-wider font-black text-[#664229]">Confirm Password</label>
              <div className="relative flex items-center">
                <Lock className="absolute left-3 w-4 h-4 text-[#664229]/50" />
                <input 
                  type={showPassword ? "text" : "password"} 
                  name="confirmPassword"
                  required
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full bg-[#F5F5DC] border border-[#664229]/20 rounded-sm py-3 pl-10 pr-4 text-xs font-sans text-[#2C1E12] focus:outline-none focus:border-[#2C1E12] transition shadow-inner"
                />
              </div>
            </div>
          )}

          {isOtpStep && (
            <div className="space-y-2 text-left animate-fade-in">
              <label className="text-[9px] uppercase tracking-wider font-black text-[#664229]">Enter 6-Digit Email Code</label>
              <div className="relative flex items-center">
                <KeyRound className="absolute left-3 w-4 h-4 text-[#664229]/50" />
                <input 
                  type="text" 
                  maxLength="6"
                  required
                  autoFocus
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full bg-[#F5F5DC] border border-[#664229]/20 rounded-sm py-3 pl-10 pr-4 text-base font-mono tracking-widest text-[#2C1E12] focus:outline-none focus:border-[#2C1E12] transition shadow-inner text-center font-bold"
                />
              </div>
              <p className="text-[8px] text-[#664229] uppercase tracking-wider text-center">
                Didn't receive code?{' '}
                <button 
                  type="button" 
                  onClick={handleAuthSubmit}
                  className="font-bold underline cursor-pointer bg-transparent border-none text-[#2C1E12]"
                >
                  Resend OTP
                </button>
              </p>
            </div>
          )}

          <button 
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3.5 bg-[#2C1E12] text-[#F5F5DC] text-[10px] uppercase tracking-[0.25em] font-black hover:opacity-95 transition disabled:opacity-40 shadow-md mt-4 border-none cursor-pointer"
          >
            {isSubmitting 
              ? 'Verifying...' 
              : isOtpStep 
                ? 'Verify & Complete Sign Up' 
                : isLoginView 
                  ? 'Log In' 
                  : 'Sign Up'}
          </button>

          {infoMessage && (
            <p className="text-center text-xs text-green-800 font-semibold bg-green-500/10 py-2 rounded-sm">
              ℹ️ {infoMessage}
            </p>
          )}

          {authError && (
            <p className="text-center text-xs text-red-800 font-bold tracking-wide animate-pulse bg-red-500/10 py-2 rounded-sm">
              ❌ {authError}
            </p>
          )}

          <div className="text-center pt-3 border-t border-[#2C1E12]/10 mt-3">
            <button
              type="button"
              onClick={() => {
                setIsLoginView(!isLoginView);
                setIsOtpStep(false);
                setAuthError('');
                setInfoMessage('');
              }}
              className="text-[10px] uppercase tracking-widest font-black text-[#664229] hover:text-[#2C1E12] transition bg-transparent border-none cursor-pointer underline underline-offset-4"
            >
              {isLoginView ? "Don't have an account? Sign Up" : "Already registered? Log In"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default CustomerAuth;